<template>
  <div class="checkout-page">
    <div class="checkout-container">
      <!-- Order Summary Section -->
      <div class="order-summary">
        <h2>Order Summary</h2>
        <div class="cart-items">
          <div v-for="item in cartItems" :key="item.id" class="cart-item">
            <img :src="item.image" :alt="item.title" class="item-image" @error="handleImageError">
            <div class="item-details">
              <h3>{{ truncate(item.title, 50) }}</h3>
              <p>Quantity: {{ item.quantity }}</p>
              <p>Price: {{ item.price }}</p>
            </div>
          </div>
        </div>
        <div class="order-totals">
          <div class="subtotal">
            <span>Subtotal:</span>
            <span>€{{ subtotal.toFixed(2) }}</span>
          </div>
          <div class="shipping">
            <span>Shipping:</span>
            <span>€{{ shippingCost.toFixed(2) }}</span>
          </div>
          <div class="total">
            <span>Total:</span>
            <span>€{{ total.toFixed(2) }}</span>
          </div>
        </div>
      </div>

      <!-- Payment Section -->
      <div class="payment-section">
        <h2>Payment Details</h2>
        <div v-if="selectedAddress" class="shipping-address">
          <h3>Shipping Address</h3>
          <p>{{ selectedAddress.name }}</p>
          <p>{{ selectedAddress.street_address }}</p>
          <p v-if="selectedAddress.apt_suite">{{ selectedAddress.apt_suite }}</p>
          <p>{{ selectedAddress.city }}, {{ selectedAddress.state }} {{ selectedAddress.postal_code }}</p>
          <p>{{ selectedAddress.country }}</p>
          <p v-if="selectedAddress.phone">Phone: {{ selectedAddress.phone }}</p>
        </div>
        <div class="payment-form">
          <div id="card-element"></div>
          <div id="card-errors" role="alert" v-if="error" class="error-message">
            {{ error }}
          </div>
          <button 
            @click="handleSubmit" 
            :disabled="loading || !stripe || !selectedAddress" 
            class="pay-button"
          >
            <span v-if="loading">Processing...</span>
            <span v-else>Pay €{{ total.toFixed(2) }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import { mapState } from 'vuex';
import axios from 'axios';
import config from '../config';

export default {
  name: 'Checkout',
  data() {
    return {
      stripe: null,
      card: null,
      error: null,
      loading: false,
      selectedAddress: null,
      addresses: [],
      paymentProcessing: false,
      // Headers handled by session-based auth
      cartItems: []
    };
  },
  computed: {
    ...mapState({
      cart: state => state.cart,
      token: state => state.auth.token,
      user: state => state.auth.user,
      isAuthenticated: state => state.auth.isAuthenticated
    }),
    subtotal() {
      return this.cartItems.reduce((sum, item) => {
        const price = parseFloat(item.price.replace(/[^0-9.-]+/g, ''));
        return sum + (price * item.quantity);
      }, 0);
    },
    shippingCost() {
      if (this.cartItems.length === 0) return 0;
      // Base shipping cost is 25€ for first item
      // Additional 10€ for each extra item
      return 25 + (Math.max(0, this.cartItems.length - 1) * 20);
    },
    total() {
      return this.subtotal + this.shippingCost;
    }
  },
  async created() {
    // Check authentication first
    if (!this.isAuthenticated) {
      this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } });
      return;
    }

    this.loading = true;
    try {
      // Simulate initial loading
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Ensure we have user data
      if (!this.user || !this.user.id) {
        try {
          // Fetch user data if not available
          const response = await axios.get(`${config.apiBaseUrl}/api/auth/check-session`, {
            withCredentials: true
          });
          
          if (!response.data.isAuthenticated || !response.data.user?.id) {
            this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } });
            return;
          }
          
          this.$store.commit('SET_USER', response.data.user);
        } catch (error) {
          console.error('Error fetching user data:', error);
          this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } });
          return;
        }
      }

      // Check if cart is empty
      if (!this.cart || this.cart.length === 0) {
        this.$router.push('/cart');
        return;
      }

      // Initialize components with loading states
      await Promise.all([
        this.initializeStripe(),
        this.fetchAddresses(),
        new Promise(resolve => setTimeout(resolve, 800)) // Minimum loading time
      ]);
      
      // Get selected address ID from localStorage
      const selectedAddressId = localStorage.getItem('selectedAddressId');
      if (selectedAddressId) {
        this.selectedAddress = this.addresses.find(addr => addr.id === parseInt(selectedAddressId));
      }
      
      // If no selected address found, try to use default address
      if (!this.selectedAddress && this.addresses.length > 0) {
        this.selectedAddress = this.addresses.find(addr => addr.is_default === 1) || this.addresses[0];
      }

      if (!this.selectedAddress) {
        this.$router.push('/addresses');
        return;
      }

      // Load cart items with parameters from localStorage
      const cartWithParams = JSON.parse(localStorage.getItem('cartWithParams') || '[]');
      this.cartItems = cartWithParams;

      // Show success message when everything is loaded
      this.$root.$emit('show-toast', {
        message: 'Checkout ready',
        type: 'success'
      });
    } catch (error) {
      console.error('Checkout initialization error:', error);
      this.error = 'Failed to initialize checkout. Please try again.';
      this.$root.$emit('show-toast', {
        message: this.error,
        type: 'error'
      });
    } finally {
      this.loading = false;
    }
  },
  methods: {

    async initializeStripe() {
      try {
        // Make sure we have the Stripe public key
        const stripeKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
        if (!stripeKey) {
          throw new Error('Payment configuration is missing');
        }
        
        // Initialize Stripe
        const stripe = await loadStripe(stripeKey);
        if (!stripe) {
          throw new Error('Payment system initialization failed');
        }
        
        this.stripe = stripe;
        const elements = stripe.elements();
        this.card = elements.create('card', {
          style: {
            base: {
              fontSize: '16px',
              color: '#32325d',
              fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
              fontSmoothing: 'antialiased',
              '::placeholder': {
                color: '#aab7c4'
              }
            },
            invalid: {
              color: '#fa755a',
              iconColor: '#fa755a'
            }
          },
          hidePostalCode: true
        });
        
        // Mount the card element and add event listener
        this.card.mount('#card-element');
        this.card.addEventListener('change', this.handleCardChange);
      } catch (error) {
        console.error('Payment initialization error');
        this.error = 'Failed to load payment form. Please try again later.';
        // Show error in the UI
        const errorDiv = document.getElementById('card-errors');
        if (errorDiv) {
          errorDiv.textContent = this.error;
        }
      }
    },
    handleCardChange(event) {
      if (event.error) {
        this.error = event.error.message;
      } else {
        this.error = null;
      }
    },
    async fetchAddresses() {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/api/addresses?_t=${Date.now()}`, {
          /*headers: {
            //'Content-Type': 'application/json'
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'X-Requested-With': 'XMLHttpRequest'            
          },*/
          withCredentials: true // Important for sending cookies
        });
        this.addresses = response.data;
        
        if (this.addresses.length === 0) {
          this.$router.push('/addresses');
          return;
        }
      } catch (error) {
        console.error('Error fetching addresses:', error);
        if (error.response?.status === 401) {
          this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } });
          return;
        }
        this.error = 'Failed to load addresses. Please try again.';
      }
    },
    async handleSubmit() {
      if (!this.stripe || !this.card || !this.selectedAddress) {
        this.error = 'Please ensure all payment details are complete';
        return;
      }

      if (this.paymentProcessing) {
        this.error = 'Payment is already being processed';
        return;
      }

      // Get fresh user data from session
      let userId;
      try {
        const response = await axios.get(`${config.apiBaseUrl}/api/auth/check-session`, {
          withCredentials: true,
          headers: this.headers
        });
        
        if (!response.data.isAuthenticated || !response.data.user?.id) {
          this.error = 'User session is invalid. Please login again.';
          this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } });
          return;
        }
        
        userId = response.data.user.id;
      } catch (error) {
        console.error('Error checking session:', error);
        this.error = 'Failed to verify user session. Please login again.';
        this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } });
        return;
      }

      this.loading = true;
      this.paymentProcessing = true;
      this.error = null;

      try {
        // Create payment intent
        const { data } = await axios.post(
          `${config.apiBaseUrl}/api/payment/create-payment-intent`,
          {
            amount: this.total,
            currency: 'eur'
          },
          {
            withCredentials: true,
            headers: this.headers
          }
        );

        if (!data.clientSecret) {
          throw new Error('No client secret received from the server');
        }

        const { error: confirmError, paymentIntent } = await this.stripe.confirmCardPayment(
          data.clientSecret,
          {
            payment_method: {
              card: this.card,
              billing_details: {
                address: {
                  city: this.selectedAddress.city,
                  country: 'US',
                  line1: this.selectedAddress.street,
                  postal_code: this.selectedAddress.postal_code,
                  state: this.selectedAddress.state
                }
              }
            }
          }
        );

        if (confirmError) {
          throw new Error(confirmError.message || 'Payment confirmation failed');
        }

        // Create order with product and category IDs
        await axios.post(
          `${config.apiBaseUrl}/api/payment/create-order`,
          {
            user_id: userId, // Use the fresh user ID from session
            address_id: this.selectedAddress.id,
            items: this.cartItems.map(item => ({
              id: item.id || item.product_id,
              category_id: item.category_id || item.categoryId,
              quantity: item.quantity,
              price: parseFloat(item.price.replace(/[^0-9.-]+/g, '')),
              title: item.title.length > 100 ? item.title.slice(0, 100) : item.title
            })),
            total_amount: this.total,
            shipping_cost: this.shippingCost,
            payment_intent_id: paymentIntent.id,
            status: paymentIntent.status === 'succeeded' ? 'paid' : 'pending'
          },
          {
            withCredentials: true,
            headers: this.headers
          }
        );

        this.$store.commit('CLEAR_CART');
        localStorage.removeItem('cartWithParams');
        this.$router.push('/order-success');
      } catch (error) {
        console.error('Payment error:', error);
        
        // Handle different types of errors with user-friendly messages
        if (error.response?.data?.error) {
          // Use the user-friendly error message from the backend
          this.error = error.response.data.error;
        } else if (error.message?.includes('No such payment_intent')) {
          this.error = 'Your card was declined. Please check your card details and try again.';
        } else {
          this.error = 'Payment failed. Please try again or contact support if the problem persists.';
        }

        // Show error toast
        this.$root.$emit('show-toast', {
          message: this.error,
          type: 'error'
        });
      } finally {
        this.loading = false;
        this.paymentProcessing = false;
      }
    },
    handleImageError(e) {
      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDUwIiBoZWlnaHQ9IjQ1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
    },
    truncate(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
    }
  }
};
</script>

<style scoped>
.checkout-page {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.checkout-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.order-summary, .payment-section {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.cart-item {
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #eee;
}

.item-image {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border-radius: 4px;
}

.order-totals {
  margin-top: 2rem;
}

.subtotal, .shipping, .total {
  display: flex;
  justify-content: space-between;
  margin: 0.5rem 0;
}

.total {
  font-weight: bold;
  font-size: 1.2rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 2px solid #eee;
}

.payment-form {
  margin-top: 2rem;
}

#card-element {
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f9f9f9;
}

.error-message {
  color: #dc3545;
  margin: 1rem 0;
}

.pay-button {
  width: 100%;
  padding: 1rem;
  margin-top: 1rem;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background 0.3s;
}

.pay-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.pay-button:hover:not(:disabled) {
  background: #45a049;
}

.shipping-address {
  margin: 1rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 0.9rem;
}

.shipping-address h3 {
  margin: 0 0 0.5rem;
  color: #333;
  font-size: 1rem;
}

.shipping-address p {
  margin: 0.2rem 0;
  color: #666;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .checkout-container {
    grid-template-columns: 1fr;
  }

  .checkout-page {
    padding: 1rem;
  }

  .item-image {
    width: 80px;
    height: 80px;
  }
}
</style> 